<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import { NModal, NButton, NAlert, NSelect, useMessage } from 'naive-ui'
import type { WikiPageDetail } from '../stores/wiki_pages'
import { useSharesStore, type ShareTtl } from '../stores/shares'
import { useRestoreFocusOnClose } from '../composables/useRestoreFocusOnClose'

// Create a short-lived public share link for one page.
// Two phases in one modal: pick a duration → create → reveal the link
// once (the URL is shown only here; only its digest is stored).

const props = defineProps<{ show: boolean; page: WikiPageDetail | null }>()
const emit = defineEmits<{ 'update:show': [value: boolean] }>()

const store = useSharesStore()
const message = useMessage()

// Return focus to the trigger when the modal closes. props.show
// isn't a writable model here, so adapt it to a Ref via toRef.
useRestoreFocusOnClose(toRef(props, 'show'))

const ttl = ref<ShareTtl>('24h')
const creating = ref(false)

const TTL_OPTIONS = [
  { label: '1 hour', value: '1h' },
  { label: '24 hours', value: '24h' },
  { label: '7 days', value: '7d' },
]

const url = computed(() => store.lastCreatedUrl)

// Reset the form each time the modal re-opens so a stale duration or a
// previous link never carries over.
watch(
  () => props.show,
  (open) => {
    if (open) {
      ttl.value = '24h'
      store.forgetLastCreatedUrl()
    }
  },
)

async function createLink() {
  if (!props.page) return
  creating.value = true
  try {
    await store.create(props.page.id, ttl.value)
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to create link')
  } finally {
    creating.value = false
  }
}

async function copy() {
  if (!url.value) return
  try {
    await navigator.clipboard.writeText(url.value)
    message.success('Share link copied')
  } catch {
    message.warning('Clipboard not available — select and copy manually')
  }
}

function close() {
  store.forgetLastCreatedUrl()
  emit('update:show', false)
}
</script>

<template>
  <NModal
    :show="show"
    preset="dialog"
    :title="url ? 'Copy this share link' : 'Share this page'"
    style="width: 640px"
    :close-on-esc="!creating"
    :mask-closable="!creating"
    @update:show="(v: boolean) => emit('update:show', v)"
  >
    <!-- Phase 1: pick a duration -->
    <template v-if="!url">
      <p class="text-sm text-white/70 m-0 mb-3">
        Generate a temporary link to
        <strong>{{ page?.title }}</strong
        >. Anyone with the link can read this page (no login) until it expires. The page title and body are visible to
        the recipient; internal links and the relations panel are not.
      </p>
      <label class="text-xs uppercase tracking-wide text-white/40">Expires after</label>
      <NSelect v-model:value="ttl" :options="TTL_OPTIONS" :disabled="creating" class="mt-1" />
    </template>

    <!-- Phase 2: reveal the link once, with a copy button right beside it -->
    <template v-else>
      <NAlert type="warning" :show-icon="false" class="mb-3">
        This link won't be shown again — copy it now. You can revoke it early from
        <code class="font-mono text-xs">Admin → Shares</code>.
      </NAlert>
      <div class="flex items-stretch gap-2">
        <pre
          class="flex-1 min-w-0 font-mono text-xs bg-black/40 rounded p-3 select-all break-all whitespace-pre-wrap m-0"
          data-testid="share-url"
          >{{ url }}</pre>
        <NButton type="primary" class="shrink-0" @click="copy" data-testid="share-copy"> Copy link </NButton>
      </div>
    </template>

    <template #action>
      <div class="flex gap-2 justify-end">
        <template v-if="!url">
          <NButton :disabled="creating" @click="close" data-testid="share-cancel">Cancel</NButton>
          <NButton type="primary" :loading="creating" :disabled="!page" @click="createLink" data-testid="share-create">
            Create link
          </NButton>
        </template>
        <template v-else>
          <NButton @click="close" data-testid="share-done">Done</NButton>
        </template>
      </div>
    </template>
  </NModal>
</template>
